import React, { useMemo, useState, useCallback } from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions, ApexAxisChartSeries } from "apexcharts";
import { fixMarkerOverlaps } from "./graphUtils";

// ── Types ─────────────────────────────────────────────────────────────────────
export interface MinPlayerGame {
  rel_offset: number;
  game_date: string;
  g_match_id: string | null;
  opponent: string;
  stat_value: number;
  is_anchor: boolean;
}

export interface MinPlayer {
  player_id: string | number;
  player_name: string;
  anchor_game_date: string;
  anchor_opponent: string;
  anchor_stat: number;
  games: MinPlayerGame[];
}

export interface MinGraphData {
  query_type: string;
  chart_type: string;
  sql_query?: string;
  labels: string[];
  values: number[];
  players: MinPlayer[];
  total_records: number;
}

interface MinGraphProps {
  data: MinGraphData;
  chartReady?: boolean;
  statLabel?: string;
}

// ── Palette — extended to 10 colours ─────────────────────────────────────────
const PALETTE = [
  "#3B82F6",
  "#10B981",
  "#F59E0B",
  "#EC4899",
  "#8B5CF6",
  "#06B6D4",
  "#F97316",
  "#14B8A6",
  "#EF4444",
  "#84CC16",
];

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&family=JetBrains+Mono:wght@400;500;600&display=swap');

  .mgr * { box-sizing: border-box; }

  .mgr {
    --bg: #ffffff;
    --surface: #f8fafc;
    --border: #e8edf3;
    --border-accent: #cbd5e1;
    --text-primary: #0f172a;
    --text-secondary: #475569;
    --text-muted: #94a3b8;
    font-family: 'Plus Jakarta Sans', sans-serif;
    background: var(--bg);
    border-radius: 24px;
    padding: 24px 28px;
    position: relative;
    width: 100%;
    border: 1px solid var(--border);
    box-shadow:
      0 1px 2px rgba(0,0,0,0.04),
      0 4px 16px rgba(0,0,0,0.06),
      0 16px 48px rgba(0,0,0,0.04);
  }

  .mgr::before {
    content: '';
    position: absolute;
    top: 0; right: 0;
    width: 260px; height: 160px;
    background: radial-gradient(ellipse at top right, rgba(59,130,246,0.05) 0%, transparent 65%);
    pointer-events: none;
    border-radius: 0 24px 0 0;
  }

  .mgr::after {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(circle, rgba(148,163,184,0.18) 1px, transparent 1px);
    background-size: 28px 28px;
    pointer-events: none;
    border-radius: 24px;
    opacity: 0.55;
    z-index: 0;
  }

  /* ── Chips ── */
  .mgr-chips-wrapper {
    position: relative;
    z-index: 1;
    margin-bottom: 18px;
  }

  .mgr-chips {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    padding: 12px 14px;
    background: var(--surface);
    border-radius: 14px;
    border: 1px solid var(--border); max-height: 164px; overflow-y: auto;
  }

  .mgr-chip-all {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 6px 13px;
    border-radius: 9px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 11px;
    font-weight: 700;
    border: 1.5px solid var(--border-accent);
    background: white;
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.18s ease;
    white-space: nowrap;
  }

  .mgr-chip-all:hover {
    background: #f0f9ff;
    border-color: #93c5fd;
    color: #2563eb;
  }

  .mgr-chip-all.active {
    background: #eff6ff;
    border-color: #93c5fd;
    color: #2563eb;
  }

  .mgr-chip {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 5px 10px 5px 8px;
    border-radius: 9px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 11px;
    font-weight: 600;
    border: 1.5px solid transparent;
    background: white;
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.18s cubic-bezier(0.4, 0, 0.2, 1);
    white-space: nowrap;
    height: 34px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  }

  .mgr-chip:hover {
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    color: var(--text-primary);
  }

  .mgr-chip.active {
    border-color: var(--chip-border);
    background: var(--chip-bg);
    color: var(--chip-color);
    box-shadow: 0 2px 8px var(--chip-shadow);
  }

  .mgr-swatch {
    width: 7px; height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }

  .mgr-chip.active .mgr-swatch {
    transform: scale(1.4);
    box-shadow: 0 0 6px var(--chip-color);
  }

  .mgr-chip-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 110px;
  }

  .mgr-chip-stat-badge {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    font-weight: 600;
    padding: 2px 7px;
    border-radius: 6px;
    background: rgba(0,0,0,0.06);
    color: var(--text-secondary);
    flex-shrink: 0;
    transition: background 0.18s ease, color 0.18s ease;
  }

  .mgr-chip.active .mgr-chip-stat-badge {
    background: var(--chip-color);
    color: #ffffff;
  }

  .mgr-chip-check {
    font-size: 10px;
    font-weight: 700;
    flex-shrink: 0;
  }

  /* ── Chart wrapper ── */
  .mgr-chart-wrapper {
    position: relative;
    z-index: 2;
    width: 100%;
    border-radius: 14px;
    overflow: visible;
    background: white; padding-top: 26px;
    border: 1px solid var(--border);
  }

  /* ── X-axis zone labels ── */
  .mgr-xaxis-labels {
    position: relative;
    z-index: 2;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 5px 28px 2px 60px; /* left matches yaxis label width */
    margin-top: 0;
  }

  .mgr-zone-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #94a3b8;
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .mgr-zone-label.prev { color: #94a3b8; }
  .mgr-zone-label.anchor { color: #3b82f6; }
  .mgr-zone-label.next  { color: #94a3b8; }

  .mgr-zone-label .zone-arrow {
    opacity: 0.6;
    font-size: 10px;
  }

  /* Force ApexCharts tooltip above everything */
  .mgr-chart-wrapper .apexcharts-tooltip {
    z-index: 9999 !important;
    overflow: visible !important;
  }

  .mgr-empty {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: var(--text-muted);
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    gap: 8px;
  }

  .mgr-empty-icon { font-size: 28px; opacity: 0.35; }
`;

// ── Component ──────────────────────────────────────────────────────────────────
const MinGraph: React.FC<MinGraphProps> = ({
  data,
  chartReady = true,
  statLabel = "Stat Value",
}) => {
  const { players } = data;

  // Default: all players selected (supports up to 10)
  const [selectedPlayers, setSelectedPlayers] = useState<Set<number>>(
    () => new Set(Array.from({ length: Math.min(2, players.length) }, (_, i) => i))
  );

  const togglePlayer = useCallback((idx: number) => {
    setSelectedPlayers(prev => {
      const next = new Set(prev);
      if (next.has(idx)) { next.delete(idx); } else if (next.size >= 2) { return prev; } else { next.add(idx); }
      return next;
    });
  }, []);

  const selectAllPlayers = useCallback(() => {
    if (selectedPlayers.size >= Math.min(2, players.length)) {
      setSelectedPlayers(new Set());
    } else {
      setSelectedPlayers(new Set(Array.from({ length: Math.min(2, players.length) }, (_, i) => i)));
    }
  }, [players.length, selectedPlayers.size]);

  const selectedIndicesOrdered = useMemo(() => Array.from(selectedPlayers), [selectedPlayers]);

  // Assign unique colors to selected players (dedupe if same slot)
  const playerColorMap = useMemo(() => {
    const map = new Map<number, string>();
    const used = new Set<number>();
    selectedIndicesOrdered.forEach((playerIdx) => {
      let slot = playerIdx % PALETTE.length;
      while (used.has(slot)) slot = (slot + 1) % PALETTE.length;
      used.add(slot);
      map.set(playerIdx, PALETTE[slot]);
    });
    return map;
  }, [selectedIndicesOrdered]);

  const series: ApexAxisChartSeries = useMemo(() => {
    return selectedIndicesOrdered.map((playerIdx) => {
      const player = players[playerIdx];
      const sorted = [...player.games].sort((a, b) => a.rel_offset - b.rel_offset);
      return {
        name: player.player_name,
        data: sorted.map((g) => ({
          x: g.rel_offset,
          y: g.stat_value,
        })),
      };
    });
  }, [players, selectedIndicesOrdered]);

  const xCategories = useMemo(() => {
    const offsets = new Set<number>();
    selectedIndicesOrdered.forEach(idx => {
      players[idx]?.games.forEach(g => offsets.add(g.rel_offset));
    });
    return Array.from(offsets).sort((a, b) => a - b);
  }, [players, selectedIndicesOrdered]);

  // Determine window boundaries for zone labels
  const minOffset = xCategories.length > 0 ? Math.min(...xCategories) : -3;
  const maxOffset = xCategories.length > 0 ? Math.max(...xCategories) : 3;

  const options: ApexOptions = useMemo(() => ({
    chart: {
      type: "line",
      background: "transparent",
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      animations: { enabled: true, speed: 650, easing: "easeinout" },
      height: 360,
      events: { mounted: (c: any) => fixMarkerOverlaps(c.el), updated: (c: any) => fixMarkerOverlaps(c.el) },
    },
    colors: selectedIndicesOrdered.map(i => PALETTE[i % PALETTE.length]),
    stroke: { curve: "smooth", width: 2.5, lineCap: "round" },
    markers: {
      size: 5,
      strokeWidth: 2.5,
      strokeColors: "#ffffff",
      strokeOpacity: 1,
      hover: { size: 6 },
      discrete: (() => {
        const anchors: any[] = [];
        selectedIndicesOrdered.forEach((playerIdx, seriesIdx) => {
          const sorted = [...players[playerIdx].games].sort((a, b) => a.rel_offset - b.rel_offset);
          const anchorDataIdx = sorted.findIndex(g => g.is_anchor);
          if (anchorDataIdx >= 0) {
            anchors.push({
              seriesIndex: seriesIdx,
              dataPointIndex: anchorDataIdx,
              fillColor: PALETTE[playerIdx % PALETTE.length],
              strokeColor: "#ffffff",
              size: 7,
            });
          }
        });
        return anchors;
      })(),
    },
    xaxis: {
      type: "numeric",
      categories: xCategories,
      labels: {
        style: {
          colors: "#94a3b8",
          fontSize: "10px",
          fontFamily: "'JetBrains Mono', monospace",
          fontWeight: "500",
        },
        formatter: (val: string) => {
          const n = Number(val);
          if (n === 0) return "MIN";
          return n > 0 ? `+${n}` : `${n}`;
        },
      },
      axisBorder: { show: true, color: "#e8edf3", height: 1 },
      axisTicks: { show: false },
      // No title here — we render it as a custom React element below
      title: { text: undefined },
    },
    yaxis: {
      labels: {
        style: {
          colors: "#94a3b8",
          fontSize: "10px",
          fontFamily: "'JetBrains Mono', monospace",
          fontWeight: "500",
        },
        formatter: (val: number) => val.toFixed(0),
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
      title: {
        text: statLabel,
        style: {
          color: "#94a3b8",
          fontSize: "10px",
          fontFamily: "'JetBrains Mono', monospace",
          fontWeight: "500",
        },
        offsetX: -4,
      },
    },
    annotations: {
      xaxis: [{
        x: 0,
        borderColor: "rgba(59,130,246,0.3)",
        borderWidth: 1.5,
        strokeDashArray: 5,
        label: {
          text: "MIN",
          position: "top",
          offsetY: -2,
          style: {
            color: "#3b82f6",
            background: "#eff6ff",
            fontSize: "11px",
            fontFamily: "'JetBrains Mono', monospace",
            fontWeight: "600",
            borderRadius: "4px",
            borderColor: "#bfdbfe",
            borderWidth: 1,
            padding: { left: 5, right: 5, top: 2, bottom: 2 },
          },
        },
      }],
    },
    grid: {
      show: true,
      borderColor: "#f1f5f9",
      strokeDashArray: 3,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 10, right: 20, bottom: 4, left: 8 },
    },
    legend: { show: false },
    tooltip: {
      theme: "light",
      shared: false,
      intersect: true,
      fixed: { enabled: false },
      custom: ({ seriesIndex, dataPointIndex }) => {
        if (seriesIndex >= selectedIndicesOrdered.length) return "";
        const playerIdx = selectedIndicesOrdered[seriesIndex];
        const player = players[playerIdx];
        const sorted = [...player.games].sort((a, b) => a.rel_offset - b.rel_offset);
        const game = sorted[dataPointIndex];
        if (!game) return "";
        const color = PALETTE[playerIdx % PALETTE.length];
        const offset = game.rel_offset;
        const offsetLabel = offset === 0 ? "MIN" : offset > 0 ? `+${offset}` : `${offset}`;

        // Dynamic sizing: base width grows with label length + value digit count
        const labelLen = statLabel.length;
        const valueLen = String(game.stat_value).length;
        const tooltipMinWidth = Math.min(300, Math.max(210, 130 + labelLen * 5 + valueLen * 10));

        return `
          <div style="
            background:#ffffff;
            border:1px solid #e2e8f0;
            border-top:3px solid ${color};
            border-radius:14px;
            padding:13px 15px;
            font-family:'Plus Jakarta Sans',sans-serif;
            box-shadow:0 8px 30px rgba(0,0,0,0.12),0 2px 8px rgba(0,0,0,0.06);
            min-width:${tooltipMinWidth}px;
            width:max-content;
            max-width:320px;
            position:relative;
            z-index:9999;
          ">
            <!-- Header: player name + offset badge -->
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
              <div style="width:8px;height:8px;border-radius:50%;background:${color};flex-shrink:0;"></div>
              <span style="font-weight:700;color:#0f172a;font-size:13px;letter-spacing:-0.01em;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">
                ${player.player_name}
              </span>
              <span style="font-family:'JetBrains Mono',monospace;font-size:10px;font-weight:600;color:${color};background:${color}15;padding:2px 8px;border-radius:6px;flex-shrink:0;">
                ${offsetLabel}
              </span>
            </div>

            <!-- Date + Opponent -->
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:10px;">
              <div style="background:#f8fafc;border-radius:8px;padding:7px 9px;border:1px solid #f1f5f9;">
                <div style="font-family:'JetBrains Mono',monospace;font-size:8px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:3px;">Date</div>
                <div style="font-size:11px;font-weight:600;color:#475569;">${game.game_date}</div>
              </div>
              <div style="background:#f8fafc;border-radius:8px;padding:7px 9px;border:1px solid #f1f5f9;">
                <div style="font-family:'JetBrains Mono',monospace;font-size:8px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:3px;">vs</div>
                <div style="font-size:11px;font-weight:600;color:#475569;">${game.opponent || "N/A"}</div>
              </div>
            </div>

            <!-- Stat: label LEFT, value RIGHT, side-by-side, dynamic gap -->
            <div style="
              display:flex;
              align-items:center;
              justify-content:space-between;
              gap:${Math.max(12, Math.min(32, 8 + labelLen * 1.2))}px;
              background:${color}10;
              border:1px solid ${color}25;
              border-radius:10px;
              padding:10px 14px;
            ">
              <!-- Full stat label — no truncation, wraps naturally -->
              <span style="
                font-family:'JetBrains Mono',monospace;
                font-size:9px;
                font-weight:600;
                color:#64748b;
                text-transform:uppercase;
                letter-spacing:0.10em;
                line-height:1.45;
                white-space:normal;
                word-break:break-word;
                flex:1;
              ">${statLabel}</span>
              <!-- Stat value — large, right-aligned, shrinks slightly for long values -->
              <span style="
                font-family:'Plus Jakarta Sans',sans-serif;
                font-size:${Math.max(20, 22 - Math.max(0, valueLen - 3) * 3)}px;
                font-weight:800;
                color:${color};
                letter-spacing:-0.03em;
                line-height:1;
                flex-shrink:0;
                text-align:right;
              ">${game.stat_value}</span>
            </div>
          </div>
        `;
      },
    },
    dataLabels: { enabled: false },
  }), [players, xCategories, statLabel, selectedIndicesOrdered]);

  if (!players || players.length === 0) {
    return (
      <>
        <style>{css}</style>
        <div className="mgr">
          <div className="mgr-empty">
            <span className="mgr-empty-icon">📭</span>
            <span>No data available</span>
          </div>
        </div>
      </>
    );
  }

  const allSelected = selectedPlayers.size === Math.min(2, players.length);

  return (
    <>
      <style>{css}</style>
      <div className="mgr">

        {/* Player chips — all 10 shown, free wrap */}
        {selectedPlayers.size >= 2 && (
  <div style={{ position:"relative", zIndex:2, marginBottom:8, fontSize:11, color:"#b45309", fontWeight:600, background:"#fffbeb", border:"1px solid #fde68a", borderRadius:6, padding:"4px 12px", textAlign:"center" }}>
    Maximum 2 players can be compared at once. Deselect a player to add another.
  </div>
)}

        <div className="mgr-chips-wrapper">
          <div className="mgr-chips">
            <button
              className={`mgr-chip-all${allSelected ? " active" : ""}`}
              onClick={selectAllPlayers}
            >
              {allSelected ? "✕ Clear" : `⊕ All (up to ${Math.min(2, players.length)})`}
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px" }}>
                ({players.length})
              </span>
            </button>

            {players.map((player, idx) => {
              const active = selectedPlayers.has(idx);
              const color = playerColorMap.get(idx) ?? "#cbd5e1";
              return (
                <button
                  key={idx}
                  className={`mgr-chip${active ? " active" : ""}`}
                  onClick={() => togglePlayer(idx)}
                  style={{
                    ["--chip-color" as any]: color,
                    ["--chip-bg" as any]: `${color}12`,
                    ["--chip-border" as any]: `${color}40`,
                    ["--chip-shadow" as any]: `${color}18`,
                  }}
                >
                  <span
                    className="mgr-swatch"
                    style={{ background: active ? color : "#cbd5e1" }}
                  />
                  <span className="mgr-chip-name">{player.player_name}</span>
                  <span className="mgr-chip-stat-badge">
                    {player.anchor_stat}
                  </span>
                  {active && (
                    <span className="mgr-chip-check" style={{ color }}>✓</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Chart */}
        <div className="mgr-chart-wrapper">
          {series.length > 0 && chartReady ? (
            <ReactApexChart
              type="line"
              series={series}
              options={options}
              height={360}
            />
          ) : (
            <div className="mgr-empty" style={{ height: 360 }}>
              <span className="mgr-empty-icon">↑</span>
              <span>Select players above to view trends</span>
            </div>
          )}
        </div>

        {/* 3-label X-axis zone row: PREV GAMES · MIN · NEXT GAMES */}
        {series.length > 0 && chartReady && (
          <div className="mgr-xaxis-labels">
            <span className="mgr-zone-label prev">
              <span className="zone-arrow">←</span>
              PREV GAMES
              {minOffset < 0 && (
                <span style={{ opacity: 0.55, fontSize: "8px" }}>({Math.abs(minOffset)})</span>
              )}
            </span>
            <span className="mgr-zone-label next">
              NEXT GAMES
              {maxOffset > 0 && (
                <span style={{ opacity: 0.55, fontSize: "8px" }}>({maxOffset})</span>
              )}
              <span className="zone-arrow">→</span>
            </span>
          </div>
        )}
      </div>
    </>
  );
};

export default MinGraph;